import React, { useState } from 'react';
import styled from 'styled-components';
import compose from 'lodash/fp/compose';
import { useDispatch } from 'react-redux';
import { withTranslation } from 'components/hoc';
import { Button, Input, Select } from 'components/common/FormItems';
import { submitBankTransactionTan } from 'actions';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
`;

const Card = styled.div`
  background: var(--admincat-color-grey-1);
  border-radius: 6px;
  width: 92%;
  max-width: 440px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
`;

const Title = styled.h4`
  color: var(--admincat-color-grey-2);
  margin-bottom: 4px;
`;

const Hint = styled.p`
  color: var(--admincat-color-grey-3);
  font-size: 13px;
  margin-bottom: 12px;
`;

const TestHint = styled.div`
  background: var(--admincat-color-grey-6);
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.9;
  color: var(--admincat-color-grey-2);
  margin-bottom: 16px;

  code {
    background: var(--admincat-color-grey-1);
    padding: 1px 6px;
    border-radius: 3px;
    font-family: monospace;
  }
`;

// Forces a vertical layout: a global `form { display: flex }` rule
// (Documents/index.css) would otherwise lay every field out in one row.
const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;

  > div {
    flex: 1;
    width: auto;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
`;

const LiveWarning = styled(TestHint)`
  background: #4a3b1e;
  color: #ffd479;

  code {
    color: #ffd479;
  }
`;

const ChallengeBox = styled.div`
  background: var(--admincat-color-grey-6);
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 16px;
  color: var(--admincat-color-grey-2);
  font-size: 13px;
  line-height: 1.6;

  img {
    display: block;
    margin: 12px auto 0;
    max-width: 220px;
  }
`;

const PROVIDER_OPTIONS = [
  { id: 'fints', name: 'FinTS' },
  { id: 'obp', name: 'Open Bank Project' },
];

const MODE_OPTIONS = [
  { id: 'demo', name: 'Demo (simulation)' },
  { id: 'live', name: 'Live bank account' },
];

// Sent to the backend; FinTS login/PIN are used in-memory only and never stored.
const FinTSExtractModal = (props) => {
  const { tr, accountIds, loading, onClose, onSubmit } = props;
  const dispatch = useDispatch();

  const [provider, setProvider] = useState('fints');
  const [mode, setMode] = useState('demo');
  const [login, setLogin] = useState('');
  const [pin, setPin] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // When the bank asks for a TAN, the modal switches to a challenge step.
  const [tanChallenge, setTanChallenge] = useState(null);
  const [tan, setTan] = useState('');

  const isFints = provider === 'fints';
  const isDemo = mode === 'demo';

  const clearAndClose = () => {
    setPin('');
    setTan('');
    setTanChallenge(null);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      account_ids: accountIds,
      provider,
      credentials: isFints ? { login: login.trim(), pin } : {},
      start_date: startDate || null,
      end_date: endDate || null,
      // Only FinTS distinguishes demo vs live; let the server default otherwise.
      ...(isFints ? { simulate: isDemo } : {}),
    };

    const result = await onSubmit(payload);
    if (result?.status === 'tan_required') {
      // Keep login/PIN in memory for the TAN step; switch to the challenge view.
      setTanChallenge(result);
      return;
    }
    clearAndClose();
  };

  const handleTanSubmit = async (e) => {
    e.preventDefault();
    const result = await dispatch(
      submitBankTransactionTan({
        account_id: tanChallenge.account_id,
        token: tanChallenge.token,
        tan: tan.trim(),
        credentials: { login: login.trim(), pin },
        start_date: startDate || null,
        end_date: endDate || null,
      }),
    );
    if (result?.status === 'tan_required') {
      // Bank issued a further challenge (e.g. decoupled app not yet approved).
      setTanChallenge(result);
      setTan('');
      return;
    }
    clearAndClose();
  };

  // --- TAN challenge step ------------------------------------------------
  if (tanChallenge) {
    const image = tanChallenge.challenge_image;
    return (
      <Overlay onClick={clearAndClose}>
        <Card onClick={(e) => e.stopPropagation()}>
          <Title>{tr('Confirm with TAN')}</Title>
          <ChallengeBox>
            {tanChallenge.challenge || tr('Your bank requires a TAN to continue.')}
            {image && (
              <img
                src={`data:${image.mime};base64,${image.data}`}
                alt={tr('TAN challenge')}
              />
            )}
          </ChallengeBox>
          <Form onSubmit={handleTanSubmit}>
            {!tanChallenge.decoupled && (
              <Input
                label="TAN"
                name="tan"
                value={tan}
                placeholder={tr('Enter the TAN from your bank')}
                onChange={({ target }) => setTan(target.value)}
              />
            )}
            {tanChallenge.decoupled && (
              <Hint>
                {tr('Approve the request in your banking app, then click Confirm.')}
              </Hint>
            )}
            <Actions>
              <Button
                type="button"
                name="Cancel"
                onClick={clearAndClose}
                disabled={loading}
              />
              <Button
                type="submit"
                name={loading ? 'Confirming...' : 'Confirm'}
                disabled={loading || (!tanChallenge.decoupled && !tan.trim())}
              />
            </Actions>
          </Form>
        </Card>
      </Overlay>
    );
  }

  return (
    <Overlay onClick={clearAndClose}>
      <Card onClick={(e) => e.stopPropagation()}>
        <Title>{tr('Extract Bank Transaction')}</Title>
        <Hint>
          {tr(
            'FinTS credentials are used only for this request and are never stored.',
          )}
        </Hint>

        {isFints && isDemo && (
          <TestHint>
            <strong>{tr('Demo / test mode')}</strong>
            <br />
            Login <code>demo</code> · PIN <code>1234</code>
            <br />
            <code>demo-tan</code> → TAN required · <code>demo-empty</code> → no
            results
          </TestHint>
        )}

        {isFints && !isDemo && (
          <LiveWarning>
            <strong>⚠ {tr('Live bank account')}</strong>
            <br />
            {tr(
              'Connects to the real bank with the login/PIN below. Most banks will require a TAN (2FA).',
            )}
          </LiveWarning>
        )}

        <Form onSubmit={handleSubmit}>
          <Select
            label="Provider"
            name="provider"
            value={provider}
            options={PROVIDER_OPTIONS}
            deselectable={false}
            onChange={({ target }) => setProvider(target.value)}
          />

          {isFints && (
            <>
              <Select
                label="Mode"
                name="mode"
                value={mode}
                options={MODE_OPTIONS}
                deselectable={false}
                onChange={({ target }) => setMode(target.value)}
              />
              <Input
                label="Login"
                name="login"
                value={login}
                placeholder={isDemo ? 'e.g. demo' : 'Online banking login'}
                onChange={({ target }) => setLogin(target.value)}
              />
              <Input
                label="PIN"
                name="pin"
                type="password"
                value={pin}
                placeholder={isDemo ? 'e.g. 1234' : 'Online banking PIN'}
                onChange={({ target }) => setPin(target.value)}
              />
            </>
          )}

          <Row>
            <Input
              label="From"
              name="start_date"
              type="date"
              value={startDate}
              onChange={({ target }) => setStartDate(target.value)}
            />
            <Input
              label="To"
              name="end_date"
              type="date"
              value={endDate}
              onChange={({ target }) => setEndDate(target.value)}
            />
          </Row>

          <Actions>
            <Button
              type="button"
              name="Cancel"
              onClick={clearAndClose}
              disabled={loading}
            />
            <Button
              type="submit"
              name={loading ? 'Extracting...' : 'Extract'}
              disabled={loading}
            />
          </Actions>
        </Form>
      </Card>
    </Overlay>
  );
};

export default compose(withTranslation)(FinTSExtractModal);
